import { Editor } from '@monaco-editor/react';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import { useColorScheme } from '@mui/material/styles';
import Switch from '@mui/material/Switch';
import Tooltip from '@mui/material/Tooltip';
import { useState } from 'react';
import { ZodJsonSchema } from '@cadstrom/cadstrom-shared';

import { useDataElTagGenerator } from '#utils/use-data-el-tag-generator.js';

import type { OnMount } from '@monaco-editor/react';
import type { json } from 'monaco-editor';
import type { IAiExtractionContextValue } from '#screens/ai-workbench/ai-extraction-context.js';

export type AiWorkbenchMessageProps<T extends object | Array<object> = object> = {
	parseResult: T;
	aiExtractionContext: IAiExtractionContextValue<T>;
};

export function AiWorkbenchMessage({ aiExtractionContext, parseResult }: AiWorkbenchMessageProps) {
	const { colorScheme } = useColorScheme();
	const [showJson, setShowJson] = useState(false);
	const [saving, setSaving] = useState(false);
	const disabledReason = aiExtractionContext.applyDisabledReason;
	const [data, setData] = useState(
		aiExtractionContext.postProcessExtractedData ?
			aiExtractionContext.postProcessExtractedData(parseResult)
		:	parseResult,
	);
	const [editorContents, setEditorContents] = useState(JSON.stringify(data, undefined, 4));
	const fileMatch = `${aiExtractionContext.configId}.json`;
	const handleMount: OnMount = (editor, monaco) => {
		if (aiExtractionContext.extractionSchema === undefined) {
			return;
		}

		const existingSchemas =
			// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- There's an error in the types of @monaco-editor/react
			(monaco.languages.json.jsonDefaults.diagnosticsOptions.schemas as json.DiagnosticsOptions['schemas']) ?? [];

		const schemas: json.DiagnosticsOptions['schemas'] = [
			// Filter out if we've already added this schema
			...existingSchemas.filter((schema) => !schema.fileMatch?.some((match) => match.includes(fileMatch))),

			{
				uri: `cadstrom://${fileMatch}`,
				schema: ZodJsonSchema.fromSchema(
					aiExtractionContext.postProcessedSchema ?? aiExtractionContext.extractionSchema,
				),
				fileMatch: [`*/${fileMatch}`],
			},
		];

		// eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access -- There's an error in the types of @monaco-editor/react
		monaco.languages.json.jsonDefaults.setDiagnosticsOptions({
			// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
			...monaco.languages.json.jsonDefaults.diagnosticsOptions,
			schemas,
		});
	};

	const el = useDataElTagGenerator(AiWorkbenchMessage);
	return (
		<div {...el()} className='ml-4 flex min-w-0 flex-col gap-2'>
			{showJson || !aiExtractionContext.renderContents ?
				<div className='relative grow'>
					<Editor
						width='100%'
						height='30vh'
						language='json'
						value={editorContents}
						theme={colorScheme === 'dark' ? 'vs-dark' : 'light'}
						options={{
							folding: true,
							minimap: { enabled: false },
							lineNumbers: 'off',
						}}
						onMount={handleMount}
						onChange={(value) => {
							if (value === undefined) {
								return;
							}

							setEditorContents(value);

							try {
								setData(JSON.parse(value) as typeof data);
							} catch {
								// Invalid JSON - do nothing
							}
						}}
						path={fileMatch}
					/>
				</div>
			:	<div className='flex min-w-0 grow flex-col contain-[inline-size]'>
					<aiExtractionContext.renderContents key={aiExtractionContext.configId} data={data} updateData={setData} />
				</div>
			}

			<div className='flex flex-wrap items-center justify-end gap-2'>
				<FormControlLabel
					label={<p>JSON</p>}
					control={
						<Switch
							checked={aiExtractionContext.renderContents ? showJson : true}
							disabled={!aiExtractionContext.renderContents}
							onChange={(event) => setShowJson(event.target.checked)}
							className='m-0 mr-1.5 ml-0.5 p-0'
						/>
					}
				/>
				{aiExtractionContext.appendContents && (
					<Tooltip enterDelay={250} title={disabledReason}>
						<span>
							<Button
								loading={saving}
								disabled={disabledReason !== undefined}
								onClick={() => {
									setSaving(true);
									const p = aiExtractionContext.appendContents?.(data)?.finally(() => setSaving(false));
									if (!p) {
										setSaving(false);
									}
								}}
							>
								<p>Add to data</p>
							</Button>
						</span>
					</Tooltip>
				)}
				<Tooltip
					enterDelay={250}
					title={
						disabledReason ??
						(aiExtractionContext.replaceHint !== undefined ?
							<div className='flex flex-col gap-2'>
								<p className='mb-1 text-gray-a11'>
									<InfoOutlinedIcon /> {aiExtractionContext.replaceHint}
								</p>
							</div>
						:	undefined)
					}
				>
					<span>
						<Button
							loading={saving}
							onClick={() => {
								setSaving(true);
								const p = aiExtractionContext.replaceContents?.(data)?.finally(() => setSaving(false));
								if (!p) {
									setSaving(false);
								}
							}}
							disabled={saving || disabledReason !== undefined}
						>
							<p>Replace data</p>
						</Button>
					</span>
				</Tooltip>
				{aiExtractionContext.replaceHint === undefined && (
					<Tooltip
						enterDelay={700}
						title={
							<div className='flex flex-col gap-2'>
								<p className='mb-1 text-gray-a11'>{aiExtractionContext.replaceHint}</p>
							</div>
						}
					>
						<InfoOutlinedIcon className='cursor-help text-gray-a11' />
					</Tooltip>
				)}
			</div>
		</div>
	);
}
